import { ReactNode } from "react";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PetsOutlinedIcon from "@mui/icons-material/PetsOutlined";
import SquareFootOutlinedIcon from "@mui/icons-material/SquareFootOutlined";
import { alpha, Box, IconButton, Tooltip, Typography } from "@mui/material";
import { BenefitDisableGate } from "../../../../../../permissions/benefits/components/gates/BenefitDisabledGate";
import { EBenefitTypes, EShowMode } from "../../../../../../permissions/benefits/enums/benefits.enums";
import { useBenefit } from "../../../../../../permissions/benefits/hooks/useBenefits";
import { BaseModalComponent } from "../../../../../common/BaseModalComponent";
import { quoteModern } from "../../style/style";
import { ScopeOfWorkView } from "./view/ScopeOfWorkView";

const clean = (value?: any) => String(value ?? "").trim();

const formatText = (value?: any) => {
  const normalized = clean(value);

  if (!normalized) return "";

  const map: Record<string, string> = {
    by_weekly: "Bi-weekly",
    by_monthly: "Bi-monthly",
    weekly: "Weekly",
    monthly: "Monthly",
    unique: "Custom price",
    one_time: "One-time",
    per_floor: "Per floor",
    per_area: "Per area",
    per_sqft: "Per sqft",
  };

  const key = normalized.toLowerCase();

  if (map[key]) return map[key];

  return normalized.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const getAdvancedChips = (scope: any) => {
  const chips: {
    label: string;
    tooltip: string;
    icon: ReactNode;
  }[] = [];

  const category = formatText(scope?.category?.type);
  const typeOfPlace = clean(scope?.typeOfPlace?.name);
  const frequency = formatText(scope?.frequencyType);
  const squareFootage = clean(scope?.squareFootage);

  if (category) {
    chips.push({
      label: category,
      tooltip: "Service category",
      icon: <HomeWorkOutlinedIcon sx={{ fontSize: 15 }} />,
    });
  }

  if (typeOfPlace) {
    chips.push({
      label: typeOfPlace,
      tooltip: "Type of place",
      icon: <HomeWorkOutlinedIcon sx={{ fontSize: 15 }} />,
    });
  }

  if (frequency) {
    chips.push({
      label: frequency,
      tooltip: "Service frequency",
      icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 15 }} />,
    });
  }

  if (squareFootage) {
    chips.push({
      label: `${squareFootage} sqft`,
      tooltip: "Total property area",
      icon: <SquareFootOutlinedIcon sx={{ fontSize: 15 }} />,
    });
  }

  return chips;
};

const getAdvancedStats = (scope: any) => {
  const stats: {
    label: string;
    value: string;
    tooltip: string;
    icon: ReactNode;
  }[] = [];

  const areas = Array.isArray(scope?.areas) ? scope.areas : [];

  const activeAreas = areas.filter((area: any) => {
    if (area?.quantity != null) {
      return Number(area.quantity) > 0;
    }

    if (!Array.isArray(area?.squareFootages)) {
      return false;
    }

    return area.squareFootages.some((item: any) => Number(item?.quantity ?? 0) > 0);
  });

  const otherAreas = Array.isArray(scope?.otherAreas) ? scope.otherAreas : [];

  const activeOtherAreas = otherAreas.filter((area: any) => {
    if (area?.quantity != null) {
      return Number(area.quantity) > 0;
    }

    if (!Array.isArray(area?.squareFootages)) {
      return false;
    }

    return area.squareFootages.some((item: any) => Number(item?.quantity ?? 0) > 0);
  });

  const specialRequests = Array.isArray(scope?.specialRequests) ? scope.specialRequests : [];

  const areaNames = activeAreas
    .map((item: any) => clean(item?.name))
    .filter(Boolean)
    .join(", ");

  const otherAreaNames = activeOtherAreas
    .map((item: any) => clean(item?.name))
    .filter(Boolean)
    .join(", ");

  const specialRequestNames = specialRequests
    .map((item: any) => clean(item?.name))
    .filter(Boolean)
    .join(", ");

  if (activeAreas.length > 0) {
    stats.push({
      label: "Areas included",
      value: `${activeAreas.length} ${activeAreas.length === 1 ? "area" : "areas"}`,
      tooltip: areaNames || "No area names",
      icon: <GridViewOutlinedIcon sx={{ fontSize: 18 }} />,
    });
  }

  if (activeOtherAreas.length > 0) {
    stats.push({
      label: "Other areas",
      value: `${activeOtherAreas.length} ${activeOtherAreas.length === 1 ? "area" : "areas"}`,
      tooltip: otherAreaNames || "No other area names",
      icon: <HomeWorkOutlinedIcon sx={{ fontSize: 18 }} />,
    });
  }

  if (specialRequests.length > 0) {
    stats.push({
      label: "Special requests",
      value: `${specialRequests.length} ${specialRequests.length === 1 ? "request" : "requests"}`,
      tooltip: specialRequestNames || "No request names",
      icon: <PetsOutlinedIcon sx={{ fontSize: 18 }} />,
    });
  }

  return stats;
};

interface AdvancedQuoteContentProps {
  service: any;
  quoteId: any;
  scopeOfWorkUrl?: string;
  role: any;
  quoteIsApproved?: boolean;
}

export const AdvancedQuoteContent = ({ service, quoteId, scopeOfWorkUrl, role, quoteIsApproved }: AdvancedQuoteContentProps) => {
  const { canUseNow: canDownloadPdf } = useBenefit(EBenefitTypes.SOW_PDF);
  const scope = service?.scope ?? service?.scopeOfWork ?? service?.sectorWorkflow ?? null;

  if (!scope) return null;

  const originalServiceName = clean(scope?.typeOfService?.name) || clean(service?.service) || clean(scope?.name) || "Service";

  const serviceNameOverride = clean(service?.serviceNameOverride) || clean(scope?.serviceNameOverride);

  const serviceName = serviceNameOverride || originalServiceName;

  const hasServiceNameOverride = Boolean(serviceNameOverride);
  const price = Number(service?.price ?? scope?.costOverride?.value ?? scope?.typeOfService?.baseRatePerHour ?? 0);

  const units = Number(service?.units ?? 1);

  const total = Number(service?.total ?? scope?.total ?? price * units);

  const measure = clean(service?.measure);

  const formattedPrice = price.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

  const formattedTotal = total.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

  const chips = getAdvancedChips(scope);
  const stats = getAdvancedStats(scope);

  return (
    <Box
      sx={{
        mt: 2,
        p: 1.5,
        borderRadius: 2.5,
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        border: `1px solid ${quoteModern.colors.line}`,
        boxShadow: quoteModern.colors.shadow,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1,
              py: 0.45,
              borderRadius: 999,
              bgcolor: quoteModern.colors.orangeSoft,
              color: quoteModern.colors.orange,
            }}
          >
            <DescriptionIcon sx={{ fontSize: 14 }} />

            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                lineHeight: 1,
              }}
            >
              Advanced serviced
            </Typography>
          </Box>

          <Typography
            sx={{
              mt: 1,
              fontSize: 17,
              fontWeight: 900,
              color: quoteModern.colors.textPrimary,
              lineHeight: 1.2,
              wordBreak: "break-word",
            }}
          >
            {serviceName}
          </Typography>

          {hasServiceNameOverride && (
            <Box
              sx={{
                mt: 0.65,
                display: "inline-flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.45,
                px: 1,
                py: 0.45,
                borderRadius: 999,
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                border: `1px solid ${quoteModern.colors.line}`,
                maxWidth: "100%",
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: quoteModern.colors.textSecondary,
                  flexShrink: 0,
                }}
              >
                Original service:
              </Typography>

              <Typography
                component="span"
                title={originalServiceName}
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: quoteModern.colors.textPrimary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {originalServiceName}
              </Typography>
            </Box>
          )}

          {chips.length > 0 && (
            <Box
              sx={{
                mt: 1.25,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
              }}
            >
              {chips.map((chip) => (
                <Tooltip key={`${chip.tooltip}-${chip.label}`} title={chip.tooltip} arrow placement="top">
                  <Box
                    sx={{
                      px: 1,
                      py: 0.6,
                      borderRadius: 999,
                      bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                      border: `1px solid ${quoteModern.colors.line}`,
                      color: quoteModern.colors.textPrimary,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.6,
                      fontSize: 12,
                      fontWeight: 800,
                      lineHeight: 1,
                      cursor: "default",
                      "& svg": {
                        color: quoteModern.colors.orange,
                      },
                    }}
                  >
                    {chip.icon}
                    {chip.label}
                  </Box>
                </Tooltip>
              ))}
            </Box>
          )}

          {stats.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(3, minmax(0, 1fr))",
                },
                gap: 1,
              }}
            >
              {stats.map((item) => (
                <Tooltip key={item.label} title={item.tooltip} arrow placement="top">
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                      border: `1px solid ${quoteModern.colors.line}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                      cursor: "default",
                      transition: "all 150ms ease",
                      "&:hover": {
                        bgcolor: alpha(quoteModern.colors.orange, 0.05),
                        borderColor: alpha(quoteModern.colors.orange, 0.24),
                        transform: "translateY(-1px)",
                        boxShadow: `0 4px 12px ${alpha(quoteModern.colors.orange, 0.08)}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.75,
                        color: quoteModern.colors.orange,
                        bgcolor: alpha(quoteModern.colors.orange, 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={quoteModern.labelSx}>{item.label}</Typography>

                      <Typography
                        sx={{
                          mt: 0.35,
                          fontSize: 12.5,
                          fontWeight: 800,
                          color: quoteModern.colors.textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 1,
            flexShrink: 0,
          }}
        >
          {/* TOTAL */}
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 18,
              lineHeight: 1.2,
              color: "#ff6b00",
            }}
          >
            {formattedTotal}
          </Typography>

          {/* ACTION BUTTONS */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <BaseModalComponent
              modalKey={`scope-${service?.serviceUid}`}
              modalLabel={`${serviceName} - Scope of Work`}
              defaultSizes={{
                width: {
                  xs: "95%",
                  sm: "90%",
                  md: "85%",
                  lg: "1100px",
                },
                height: {
                  xs: "90%",
                  sm: "85%",
                  md: "85%",
                  lg: "90%",
                },
              }}
              elementTrigger={
                <Tooltip title="View scope details">
                  <span>
                    <IconButton
                      size="small"
                      sx={{
                        color: quoteModern.colors.orange,
                        bgcolor: quoteModern.colors.orangeSoft,
                        border: `1px solid ${quoteModern.colors.orangeSoft}`,
                        borderRadius: 2,
                        width: 36,
                        height: 36,
                        "&:hover": {
                          bgcolor: alpha(quoteModern.colors.orange, 0.12),
                          borderColor: quoteModern.colors.orange,
                        },
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              }
            >
              <ScopeOfWorkView quoteId={quoteId} serviceId={service?.serviceUid} />
            </BaseModalComponent>

            {!(role === "client" && !canDownloadPdf) && (
              <Tooltip title={scopeOfWorkUrl ? "Download scope PDF" : "PDF not available"}>
                <span>
                  <BenefitDisableGate
                    benefit={EBenefitTypes.SOW_PDF}
                    overlay
                    showWhenBlocked={EShowMode.DISABLED}
                    blockedTooltip="Upgrade your plan to unlock this feature"
                  >
                    <IconButton
                      component={scopeOfWorkUrl ? "a" : "button"}
                      target={scopeOfWorkUrl ? "_blank" : undefined}
                      rel={scopeOfWorkUrl ? "noopener noreferrer" : undefined}
                      href={scopeOfWorkUrl || undefined}
                      disabled={!scopeOfWorkUrl}
                      size="small"
                      sx={{
                        color: "#FFFFFF",
                        bgcolor: quoteModern.colors.orange,
                        border: `1px solid ${quoteModern.colors.orange}`,
                        borderRadius: 2,
                        width: 36,
                        height: 36,
                        "&:hover": {
                          bgcolor: quoteModern.colors.orangeHover,
                          borderColor: quoteModern.colors.orangeHover,
                        },
                        "&:disabled": {
                          bgcolor: "#CBD5E1",
                          borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                          color: "#FFFFFF",
                        },
                      }}
                    >
                      <OpenInNewIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </BenefitDisableGate>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
